import { useEffect, useState } from 'preact/hooks';
import { routeStore } from '../state/routeStore';
import {
  closeWelcomeSheet,
  openWelcomeSheet,
  setWatcherName,
  watcherName,
  welcomeSheetOpen,
} from '../state/welcomeSheet';
import { updateName } from '../backend/session';
import { t } from '../i18n';
import clsx from 'clsx';

/**
 * Non-blocking name prompt. Two ways it appears:
 *
 *  1. Auto-opens on first visit when no `watcherName` is stored — the
 *     watcher is asked to introduce themselves to the traveller.
 *  2. Re-opens when the user taps "Watching as …" in the drawer so they
 *     can edit the name they're showing as. This name lands on the
 *     traveller's mobile companion list via `PUT /v3/user` + the existing
 *     `companion` socket broadcast — the load-bearing mechanism the
 *     rebuild preserves.
 */
export function WelcomeSheet() {
  const route = routeStore.value;
  const open = welcomeSheetOpen.value;
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // First-visit auto-open. We don't watch the signal directly — auto-open is
  // a one-shot per route load, not a continuous binding.
  useEffect(() => {
    if (!route) return;
    if (watcherName.value !== null) return;
    const handle = setTimeout(() => openWelcomeSheet(), 700);
    return () => clearTimeout(handle);
  }, [route?.id]);

  // When the sheet opens, prefill the input with whatever name we already
  // have on file. Editing path lands here; the empty-string case (skipped
  // earlier) stays empty so the placeholder text shows through.
  useEffect(() => {
    if (!open) return;
    setValue(watcherName.value ?? '');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeWelcomeSheet();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open || !route) return null;

  const travellerName = route.creator?.name?.trim() || route.creator?.username || '—';

  async function submit(name: string | null) {
    setSubmitting(true);
    try {
      if (name && name.trim()) {
        const trimmed = name.trim().slice(0, 60);
        await updateName(trimmed);
        setWatcherName(trimmed);
      } else {
        setWatcherName('');
      }
    } catch {
      // network failed — close anyway; the watcher can retry from the
      // drawer's "Watching as …" row.
    } finally {
      setSubmitting(false);
      closeWelcomeSheet();
    }
  }

  return (
    <div
      class="pointer-events-auto fixed inset-0 z-30 flex items-center justify-center px-3 motion-safe:animate-[fadeIn_0.25s_ease-out]"
      role="dialog"
      aria-modal="false"
      aria-labelledby="welcome-heading"
    >
      <div class="w-full max-w-md rounded-2xl border border-border bg-surface p-4 shadow-2xl">
        <h2 id="welcome-heading" class="text-base font-semibold text-primary">
          {t('welcome.prompt', { name: travellerName })}
        </h2>
        <p class="mt-1 text-sm text-text-secondary">{t('welcome.askName')}</p>
        <form
          class="mt-3 flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void submit(value);
          }}
        >
          <label class="text-xs font-medium text-text-secondary" for="welcome-name">
            {t('welcome.nameLabel')}
          </label>
          <input
            id="welcome-name"
            type="text"
            value={value}
            maxLength={60}
            disabled={submitting}
            onInput={(event) => setValue((event.target as HTMLInputElement).value)}
            class="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-secondary/30"
          />
          <div class="mt-1 flex gap-2">
            <button
              type="submit"
              disabled={submitting}
              class={clsx(
                'flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-opacity',
                submitting && 'opacity-60',
              )}
            >
              {t('welcome.save', { name: travellerName })}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => void submit(null)}
              class="rounded-lg border border-border bg-surface px-4 py-2 text-sm text-text-secondary hover:text-text"
            >
              {t('welcome.skip')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

